import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { extractApiErrorMessage } from '@/lib/api-client';
import { useCancelInvoiceMutation } from '../api/use-cancel-invoice-mutation';
import { invoiceActionAvailable } from '../lib/invoice-actions';
import { resolveNoteKind } from '../lib/invoice-label';
import type { InvoiceRow } from '../types';

const MIN_REASON_LENGTH = 5;

function cancelLabel(invoice: InvoiceRow): string {
  const noteKind = resolveNoteKind(invoice);
  if (noteKind !== null) return noteKind === 'Debit' ? 'Debit Note' : 'Credit Note';
  if (invoice.invoice_type === 'Advance') return 'Advance invoice';
  if (invoice.invoice_type === 'Provisional') return 'Provisional invoice';
  if (invoice.invoice_type === 'Final_Settlement' || invoice.invoice_type === 'Final Settlement') {
    return 'Final Settlement invoice';
  }
  return 'invoice';
}

export interface CancelInvoiceDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly invoice: InvoiceRow;
  readonly onCancelled?: (() => void) | undefined;
}

interface CancelFormProps {
  readonly reason: string;
  readonly setReason: (value: string) => void;
  readonly canSubmit: boolean;
  readonly isPending: boolean;
  readonly onDismiss: () => void;
  readonly onSubmit: () => void;
}

function CancelForm({
  reason,
  setReason,
  canSubmit,
  isPending,
  onDismiss,
  onSubmit,
}: CancelFormProps) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit();
      }}
      className="space-y-3"
    >
      <div className="flex flex-col gap-1">
        <Label htmlFor="cancel_reason">Reason</Label>
        <Textarea
          id="cancel_reason"
          required
          minLength={MIN_REASON_LENGTH}
          maxLength={2000}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
          disabled={isPending}
          rows={4}
          data-testid="cancel-invoice-reason"
        />
        <p className="text-muted-foreground text-xs">At least {MIN_REASON_LENGTH} characters.</p>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onDismiss} disabled={isPending}>
          Keep draft
        </Button>
        <Button
          type="submit"
          variant="destructive"
          disabled={!canSubmit}
          data-testid="cancel-invoice-confirm"
        >
          {isPending ? 'Cancelling…' : 'Cancel draft'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function CancelInvoiceDialog(props: CancelInvoiceDialogProps) {
  const { open, onOpenChange, invoice, onCancelled } = props;
  const label = cancelLabel(invoice);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const mutation = useCancelInvoiceMutation({
    invoiceId: invoice.id,
    orderId: invoice.order_id ?? '',
    blOrderId: invoice.bl_order_id ?? undefined,
  });
  const trimmed = reason.trim();
  const canSubmit = trimmed.length >= MIN_REASON_LENGTH && !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Cancel this {label}?</DialogTitle>
          <DialogDescription>
            The draft is moved to Cancelled — a terminal state that keeps the row visible for audit
            but excludes it from outstanding math and downstream flows. Provide a reason for the
            audit trail.
          </DialogDescription>
        </DialogHeader>
        <CancelForm
          reason={reason}
          setReason={setReason}
          canSubmit={canSubmit}
          isPending={mutation.isPending}
          onDismiss={() => {
            onOpenChange(false);
          }}
          onSubmit={() => {
            mutation.mutate(trimmed, {
              onSuccess: () => {
                toast.success(`${label} cancelled.`);
                onCancelled?.();
                onOpenChange(false);
              },
              onError: (err) => {
                toast.error(extractApiErrorMessage(err, `Failed to cancel the ${label}.`));
              },
            });
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

export interface CancelDraftButtonProps {
  readonly invoice: InvoiceRow;
  readonly onCancelled?: (() => void) | undefined;
  readonly compact?: boolean | undefined;
}

export function CancelDraftButton(props: CancelDraftButtonProps) {
  const { invoice, onCancelled, compact } = props;
  const [open, setOpen] = useState(false);
  if (!invoiceActionAvailable(invoice, 'cancel')) return null;
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={compact ? 'h-7 gap-1 px-3 text-xs' : undefined}
        data-testid={`invoice-${invoice.id}-cancel`}
        onClick={() => {
          setOpen(true);
        }}
      >
        Cancel
      </Button>
      <CancelInvoiceDialog
        open={open}
        onOpenChange={setOpen}
        invoice={invoice}
        onCancelled={onCancelled}
      />
    </>
  );
}
